import * as cornerstoneTools from "@cornerstonejs/tools";
const {
  ToolGroupManager,
  Enums: csToolsEnums,
  CrosshairsTool,
  StackScrollTool,
  TrackballRotateTool,
  ZoomTool,
  PanTool,
  segmentation,
} = cornerstoneTools;

const {
  BrushTool,
  RectangleScissorsTool,
  CircleScissorsTool,
  PaintFillTool,
  SplineContourSegmentationTool,
} = cornerstoneTools;

const { MouseBindings, KeyboardBindings } = csToolsEnums;

/**
 * Sets up the tool groups for 2D and 3D viewports
 * @param toolGroupId The ID for the 2D viewport tool group
 * @param volume3dToolGroupId The ID for the 3D viewport tool group
 * @param editorMode Whether to add segmentation tools for editing
 * @returns The created tool groups
 */
export const setupToolGroups = async (
  toolGroupId: string,
  volume3dToolGroupId: string,
  editorMode: boolean = false,
) => {
  // Setup 2D tool group
  const toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

  // Add tools to 2D toolgroup
  cornerstoneTools.addTool(StackScrollTool);
  cornerstoneTools.addTool(CrosshairsTool);
  cornerstoneTools.addTool(PanTool);
  cornerstoneTools.addTool(ZoomTool);

  toolGroup.addTool(StackScrollTool.toolName);
  toolGroup.addTool(CrosshairsTool.toolName);
  toolGroup.addTool(PanTool.toolName);
  toolGroup.addTool(ZoomTool.toolName);

  // Add segmentation tools if in editor mode
  if (editorMode) {
    cornerstoneTools.addTool(BrushTool);
    cornerstoneTools.addTool(RectangleScissorsTool);
    cornerstoneTools.addTool(CircleScissorsTool);
    cornerstoneTools.addTool(PaintFillTool);
    cornerstoneTools.addTool(SplineContourSegmentationTool);

    // Add brush tool instances with different strategies
    toolGroup.addToolInstance("CircularBrush", BrushTool.toolName, {
      activeStrategy: "FILL_INSIDE_CIRCLE",
    });
    toolGroup.addToolInstance("CircularEraser", BrushTool.toolName, {
      activeStrategy: "ERASE_INSIDE_CIRCLE",
    });
    toolGroup.addToolInstance("SphereBrush", BrushTool.toolName, {
      activeStrategy: "FILL_INSIDE_SPHERE",
    });
    toolGroup.addToolInstance("EdgeDetectBrush", BrushTool.toolName, {
      activeStrategy: "FILL_INSIDE_CIRCLE",
      preview: {
        enabled: false,
      },
    });

    toolGroup.addTool(RectangleScissorsTool.toolName);
    toolGroup.addTool(CircleScissorsTool.toolName);
    toolGroup.addTool(PaintFillTool.toolName);
    toolGroup.addTool(SplineContourSegmentationTool.toolName);
  }

  // Set active tools for 2D views
  toolGroup.setToolActive(CrosshairsTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });
  toolGroup.setToolActive(StackScrollTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Wheel }],
  });
  toolGroup.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Primary }],
  });
  toolGroup.setToolActive(ZoomTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Wheel,
        modifierKey: KeyboardBindings.Alt,
      },
    ],
  });

  // Setup 3D tool group
  const volume3dToolGroup =
    ToolGroupManager.createToolGroup(volume3dToolGroupId);

  // Add 3D interaction tools
  cornerstoneTools.addTool(TrackballRotateTool);
  cornerstoneTools.addTool(ZoomTool);
  cornerstoneTools.addTool(PanTool);

  volume3dToolGroup.addTool(TrackballRotateTool.toolName);
  volume3dToolGroup.addTool(ZoomTool.toolName);
  volume3dToolGroup.addTool(PanTool.toolName);

  // Set active tools for 3D view
  volume3dToolGroup.setToolActive(TrackballRotateTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Primary }],
  });
  volume3dToolGroup.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });

  return { toolGroup, volume3dToolGroup };
};
